const MAX_LEVELS_DEFAULT = 200;

export const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

export const isValidLevel = (price, qty) => {
  const p = toNum(price);
  const q = toNum(qty);
  return Number.isFinite(p) && Number.isFinite(q) && p > 0 && q >= 0;
};

export const normalizeSide = (levels) => {
  if (!Array.isArray(levels)) return [];
  const out = [];
  for (const lvl of levels) {
    if (!Array.isArray(lvl) || lvl.length < 2) continue;
    const [p, q] = lvl;
    if (!isValidLevel(p, q)) continue;
    // keep as strings to preserve precision; qty=0 is meaningful for diffs (delete)
    out.push([String(p), String(q)]);
  }
  return out;
};

export const sortSide = (side, levels) => {
  const dir = side === "bids" ? -1 : 1; // bids DESC, asks ASC
  return levels
    .slice()
    .sort((a, b) => (toNum(a[0]) - toNum(b[0])) * dir);
};

export const normalizeSnapshot = (snapshot, maxLevels = MAX_LEVELS_DEFAULT) => {
  const asks = sortSide("asks", normalizeSide(snapshot?.asks))
    .filter(([, q]) => toNum(q) > 0)
    .slice(0, maxLevels);
  const bids = sortSide("bids", normalizeSide(snapshot?.bids))
    .filter(([, q]) => toNum(q) > 0)
    .slice(0, maxLevels);

  return {
    lastUpdateId: Number(snapshot?.lastUpdateId) || 0,
    asks,
    bids,
  };
};

// Apply a single Binance-style depth diff update to an existing book.
// update must contain {U, u, a, b}
export const applyDiffUpdate = (
  prevBook,
  update,
  { maxLevels = MAX_LEVELS_DEFAULT } = {},
) => {
  const book = prevBook || { asks: [], bids: [], lastUpdateId: 0 };
  const asks = book.asks || [];
  const bids = book.bids || [];

  const next = {
    ...book,
    asks: mergeSide("asks", asks, update?.a, maxLevels),
    bids: mergeSide("bids", bids, update?.b, maxLevels),
    lastUpdateId: update?.u ?? book.lastUpdateId,
  };

  return next;
};

export const mergeSide = (side, prevLevels, updates, maxLevels) => {
  const prev = Array.isArray(prevLevels) ? prevLevels : [];
  const map = new Map();

  for (const [p, q] of prev) {
    if (!isValidLevel(p, q)) continue;
    if (toNum(q) <= 0) continue;
    map.set(String(p), String(q));
  }

  const up = normalizeSide(updates);
  for (const [p, q] of up) {
    const qty = toNum(q);
    if (!Number.isFinite(qty)) continue;
    if (qty <= 0) {
      map.delete(p);
    } else {
      map.set(p, q);
    }
  }

  const merged = Array.from(map.entries()).map(([p, q]) => [p, q]);
  return sortSide(side, merged).slice(0, maxLevels);
};

export const calculateCumulative = (levels) => {
  if (!Array.isArray(levels) || levels.length === 0) return [];
  const out = new Array(levels.length);
  let sum = 0;
  for (let i = 0; i < levels.length; i += 1) {
    const [p, q] = levels[i];
    const pn = toNum(p);
    const qn = toNum(q);
    if (!Number.isFinite(pn) || !Number.isFinite(qn) || pn <= 0 || qn <= 0) {
      out[i] = sum;
      continue;
    }
    sum += pn * qn;
    out[i] = sum;
  }
  return out;
};

export const calculateTotals = (levels) => {
  const cumulative = calculateCumulative(levels);
  const total = cumulative.length ? cumulative[cumulative.length - 1] : 0;
  return { cumulative, total };
};

// Binance sync helpers
export const isValidDiffEvent = (evt) => {
  if (!evt || typeof evt !== "object") return false;
  const U = Number(evt.U);
  const u = Number(evt.u);
  if (!Number.isFinite(U) || !Number.isFinite(u)) return false;
  if (!Array.isArray(evt.a) || !Array.isArray(evt.b)) return false;
  if (U <= 0 || u <= 0) return false;
  if (u < U) return false;
  return true;
};

export const shouldSkipBySequence = (evt, lastUpdateId) => {
  const u = Number(evt?.u);
  return Number.isFinite(u) && Number.isFinite(lastUpdateId) && u <= lastUpdateId;
};

export const hasGap = (evt, lastUpdateId) => {
  const U = Number(evt?.U);
  return Number.isFinite(U) && Number.isFinite(lastUpdateId) && U > lastUpdateId + 1;
};

